import { Component, EventEmitter, Input, Output, OnInit, ElementRef, ViewChild, QueryList, ViewChildren, signal, computed, OnChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Guid } from 'guid-typescript';
import { ToastrService } from 'ngx-toastr';
import { PdfUploadValue } from '../../../../../models/helper-classes.model';
import { EDocsService } from '../../../../../services/edocs.service';
import { Enums } from '../../../../../shared/enums';
import { Utils } from '../../../../../shared/utils';
import { PdfPreviewDialogComponent } from '../../../../pdf-preview-dialog/pdf-preview-dialog.component';
import { IMPORTS } from './_imports';
import { ConfirmationService } from 'primeng/api';

@Component({
    selector: 'pdf-upload-multiple',
    templateUrl: './pdf-upload-multiple.component.html',
    styleUrl: './pdf-upload-multiple.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class PdfUploadMultipleComponent implements OnInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('anchorFileRef') anchorFileRef: ElementRef;
    @ViewChildren('fileRef') fileRef: QueryList<ElementRef>;
    // #endregion

    // #region [const]
    MAX_SIZE_MB = 100 as const;
    // #endregion

    // #region [props]
    fileRefsFile: any[] = [];
    shouldPreventUploadArray: boolean[] = [false];
    shouldPreventUploadCurrent = false;
    // #endregion

    // #region [signals]
    $maxFiles = signal(0);
    $disabled = signal(false);
    $value = signal(null as any);
    $msCurrent = signal(0);
    $msArray = signal([0]);
    $render = signal(true);
    $fileNames = signal([] as string[]);
    $activeButtonArray = signal(['main'] as ('main' | 'prevent' | 'cancelled' | 'progress' | 'done')[]);
    $activeButtonCurrent = signal('main' as ('main' | 'prevent' | 'cancelled' | 'progress' | 'done'));
    // #endregion

    // #region [computeds]
    $hasValue = computed(() => this.$value() != null && JSON.stringify(this.$value()) != '{}');
    $fileEntries = computed(() => [...Array((this.$value().fileName || [0]).length).keys()]);
    $candAddMoreFiles = computed(() =>
        // caso o componente não tenha terminado de carregar ainda
        !this.$hasValue()
        // limite de arquivos definido no Construtor de Formulário
        || this.$value()?.fileName?.length < this.$maxFiles()
    );
    $$getProgressState = (idx?: number) => computed(() => {
        if (idx != null) {
            if (this.$msArray()[idx] <= 333) {
                return 'start';
            } else if (this.$msArray()[idx] <= 666) {
                return 'half';
            }
            return 'end';
        } else {
            if (this.$msCurrent() <= 333) {
                return 'start';
            } else if (this.$msCurrent() <= 666) {
                return 'half';
            }
            return 'end';
        }
    });
    // #endregion

    // #region [Input/Output]
    @Input('maxFiles') _maxFiles: number;
    @Input('disabled') _disabled: boolean;
    @Input('value') _value: any;
    @Output('valueChange') _valueChange = new EventEmitter<PdfUploadValue>();
    // #endregion

    constructor(
        private dialog: MatDialog,
        private toastr: ToastrService,
        private confirmationService: ConfirmationService,
        private eDocsService: EDocsService
    ) { }

    ngOnInit() {
        // contorno de bug do Formio: aguarda por até 2s para que o "value" esteja disponível.
        // Lifecycle do Angular não funciona como deveria pois o componente existe no
        // contexto do Formio e recebe os valores nos parâmetros de @Input assincronamente
        this.$value.set(this.$value() || {});
        let count = -1;
        let interval = setInterval(() => {
            count++;

            if (this.$value()?.fileName?.length > 0) {
                this.$value().fileName.forEach(x => {
                    Utils.updSignal(this.$fileNames, xx => {
                        xx.push(x);
                        return xx;
                    });
                    this.shouldPreventUploadArray.push(false);
                    Utils.updSignal(this.$activeButtonArray, x => {
                        x.push('main');
                        return x;
                    });
                    Utils.updSignal(this.$msArray, x => {
                        x.push(0);
                        return x;
                    });
                });
                clearInterval(interval);
            }

            if (count == 10) {
                this.$value.set({
                    isCustomFileComponent: true,
                    fileName: [],
                    minioKey: []
                });

                clearInterval(interval);
            }
        }, 200);
    }

    ngOnChanges() {
        this.$maxFiles.set(this._maxFiles);
        this.$disabled.set(this._disabled);
        this.$value.set(this._value);
    }

    changeFile(event, idx?: number) {
        const file = event.target.files[0] as File;

        // caso a extensão do arquivo não seja PDF
        if (file.name.split('.').slice(-1)[0].toLowerCase() != 'pdf') {
            // reinicia valores do input e do componente Formio
            this.removeFile(idx ?? 0);
            this.toastr.error(Enums.Messages.PdfFilesOnly, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        // caso o tamanho do arquivo seja maior que o máximo estabelecido
        if (file.size > this.MAX_SIZE_MB * 1024 * 1024) {
            // reinicia valores do input e do componente Formio
            this.removeFile(idx ?? 0);
            this.toastr.error(Enums.Messages.PdfMaxSizeLimit.replace('{0}', this.MAX_SIZE_MB.toString()), Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        // reserva um spot na fila de upload de arquivos
        let fileQueueSpot = Guid.create().toString();
        window['_$_fileQueue'] = window['_$_fileQueue'] || [];
        window['_$_fileQueue'].push(fileQueueSpot);

        let fileReader = new FileReader();
        fileReader.readAsArrayBuffer(file);
        fileReader.onloadend = () => {
            // exibe o botão para cancelar o upload
            this.setActiveButton('prevent', idx);

            setTimeout(async () => {
                // clicou-se para cancelar o upload
                if (this.getShouldPreventUpload(idx)) {
                    this.setShouldPreventUpload(false, idx);
                    this.setActiveButton('main', idx);
                    return;
                }

                // início da fase de loading
                this.setActiveButton('progress', idx);
                let interval = setInterval(() => {
                    this.setMs(new Date().getMilliseconds(), idx);
                }, 100);

                // #region [upload do arquivo no Minio]
                const response_Get = await this.eDocsService.getGerarUrl(file.size);

                if (!response_Get.isSuccess) {
                    this.toastr.error(response_Get.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                let gerarUrl = response_Get.data;

                // monta um FormData para facilitar o POST do arquivo
                let formData = new FormData();
                for (let key in gerarUrl.body) {
                    formData.append(key, gerarUrl.body[key])
                }

                // binário tem que vir depois dos parâmetros no payload se não o Minio do E-Docs explode
                formData.append('file', file);

                // realiza o POST via fetch para facilitar o envio de conteúdo "multipart/form-data"
                const response_Post = await fetch(gerarUrl.url, {
                    method: 'post',
                    body: formData
                });

                if (!response_Post.ok) {
                    this.toastr.error(
                        Enums.Messages.MinioPostError.replace('{0}', response_Post.status.toString()).replace('{1}', response_Post.statusText),
                        Enums.Messages.Error,
                        Utils.getToastrErrorOptions()
                    );
                    return;
                }
                // #endregion

                // libera o spot na fila de upload de arquivos
                window['_$_fileQueue'] = (window['_$_fileQueue'] || []).filter(x => x != fileQueueSpot);

                // fim da fase de loading
                clearInterval(interval);
                this.setActiveButton('done', idx);

                setTimeout(() => {
                    // retorno ao modo default
                    this.setActiveButton('main', idx);
                    Utils.updSignal(this.$activeButtonArray, x => {
                        x.push('main');
                        return x;
                    });
                    this.shouldPreventUploadArray.push(false);

                    // registro e publicação das alterações
                    if (JSON.stringify(this.$value()) == '{}') {
                        this.$value.set(null);
                    }

                    this.$value.set(this.$value() || {
                        isCustomFileComponent: true,
                        fileName: [],
                        minioKey: []
                    });

                    if (idx != null) {
                        Utils.updSignal(this.$fileNames, x => {
                            x[idx] = file.name;
                            return x;
                        });
                        Utils.updSignal(this.$value, x => {
                            x.fileName[idx] = file.name;
                            x.minioKey[idx] = gerarUrl.identificadorTemporarioArquivoNaNuvem;
                            return x;
                        });
                    } else {
                        Utils.updSignal(this.$fileNames, x => {
                            x.push(file.name);
                            return x;
                        });
                        Utils.updSignal(this.$value, x => {
                            x.fileName.push(file.name);
                            x.minioKey.push(gerarUrl.identificadorTemporarioArquivoNaNuvem);
                            return x;
                        });
                    }

                    if (idx != null) {
                        let fileRefs = this.fileRef.toArray();
                        this.fileRefsFile[idx] = fileRefs[idx].nativeElement.files[0];
                    } else {
                        this.fileRefsFile.push(this.anchorFileRef.nativeElement.files[0]);
                    }

                    this.anchorFileRef.nativeElement.value = '';

                    this._valueChange.emit(this.$value());
                }, 2000);
            }, 3000);
        };
    }

    confirmRemoveFile(idx: number) {
        this.confirmationService.confirm({
            message: Enums.Messages.ConfirmRemoveFile,
            accept: () => this.removeFile(idx),
            reject: () => this.confirmationService.close()
        });
    }

    removeFile(idx: number) {
        Utils.updSignal(this.$fileNames, x => {
            x.splice(idx, 1);
            return x;
        });
        this.fileRefsFile.splice(idx, 1);
        Utils.updSignal(this.$value, x => {
            x.fileName.splice(idx, 1);
            return x;
        });

        if (this.$value().fileName.length == 0) {
            this.$value.set({});
        } else {
            Utils.updSignal(this.$value, x => {
                x.minioKey.splice(idx, 1);
                return x;
            });
        }

        this._valueChange.emit(this.$value());

        this.recycleRender();
    }

    viewFile(idx: number) {
        this.dialog.open(PdfPreviewDialogComponent, {
            data: {
                content: URL.createObjectURL(this.fileRefsFile[idx])
            }
        });
    }

    preventUpload(idx?: number) {
        if (idx != null) {
            this.shouldPreventUploadArray[idx] = true;
            Utils.updSignal(this.$activeButtonArray, x => {
                x[idx] = 'cancelled';
                return x;
            });
        } else {
            this.shouldPreventUploadCurrent = true;
            this.$activeButtonCurrent.set('cancelled');
        }

        window['_$_fileQueue'].pop();
        this.recycleRender();
    }

    // #region [===== private methods =====]
    // #endregion

    private getShouldPreventUpload(idx?: number): boolean {
        if (idx != null) {
            return this.shouldPreventUploadArray[idx];
        }

        return this.shouldPreventUploadCurrent;
    }

    private setShouldPreventUpload(value, idx?: number) {
        if (idx != null) {
            this.shouldPreventUploadArray[idx] = value;
        } else {
            this.shouldPreventUploadCurrent = value;
        }
    }

    private setActiveButton(value, idx?: number) {
        if (idx != null) {
            Utils.updSignal(this.$activeButtonArray, x => {
                x[idx] = value;
                return x;
            });
        } else {
            this.$activeButtonCurrent.set(value);
        }
    }

    private setMs(value, idx?: number) {
        if (idx != null) {
            Utils.updSignal(this.$msArray, x => {
                let copy = [...x];
                copy[idx] = value;
                return copy;
            });
        } else {
            this.$msCurrent.set(value);
        }
    }

    private recycleRender() {
        this.$render.set(false);
        setTimeout(() => this.$render.set(true), 100);
    }
}
